import {formatPrice} from '../lib/format-price'
import type {ProjectBillingMethod, ProjectBillingTerms} from '../projects/schemas'

const BILLING_METHOD_LABELS: Record<ProjectBillingMethod, string> = {
    line_rates: 'Each line at its own rate',
    hourly_transportation: 'All-in hourly',
    per_load: 'Per load',
    flat_rate: 'Flat rate',
}

export function projectBillingMethodLabel(method: ProjectBillingMethod): string {
    return BILLING_METHOD_LABELS[method]
}

const NO_TRANSPORT_RATE = 'None, each line billed at its own rate'

function projectBillingRateLabel(
    terms: Pick<
        ProjectBillingTerms,
        'billingMethod' | 'transportationHourlyRate' | 'ratePerLoad' | 'flatRateAmount'
    >,
): string | null {
    switch (terms.billingMethod) {
        case 'line_rates':
            return NO_TRANSPORT_RATE
        case 'hourly_transportation':
            return terms.transportationHourlyRate
                ? `${formatPrice(terms.transportationHourlyRate)}/hr`
                : NO_TRANSPORT_RATE
        case 'per_load':
            return terms.ratePerLoad != null ? `${formatPrice(terms.ratePerLoad)} per load` : null
        case 'flat_rate':
            return terms.flatRateAmount != null ? `${formatPrice(terms.flatRateAmount)} flat` : null
    }
}

export function projectBillingTermsLabel(terms: ProjectBillingTerms | null | undefined): string {
    return terms == null ? 'Unknown terms' : projectBillingMethodLabel(terms.billingMethod)
}

function formatDisposalUnit(unit: string): string {
    return unit.charAt(0).toUpperCase() + unit.slice(1)
}

export type BillingTermsDisplayLine = {
    label: string
    value: string
}

export function getProjectBillingTermsDisplayLines(
    terms: ProjectBillingTerms,
): BillingTermsDisplayLine[] {
    const lines: BillingTermsDisplayLine[] = [
        {label: 'Method', value: projectBillingMethodLabel(terms.billingMethod)},
    ]

    if (terms.billingMethod === 'hourly_transportation') {
        lines.push({
            label: 'All-in hourly rate',
            value: terms.transportationHourlyRate
                ? formatPrice(terms.transportationHourlyRate)
                : NO_TRANSPORT_RATE,
        })
        if (terms.estimatedTransportationHours != null) {
            lines.push({
                label: 'Estimated transportation hours',
                value: String(terms.estimatedTransportationHours),
            })
        }
    } else if (terms.billingMethod === 'per_load') {
        if (terms.ratePerLoad != null) {
            lines.push({label: 'Rate per load', value: formatPrice(terms.ratePerLoad)})
        }
        if (terms.perLoadNotes) {
            lines.push({label: 'Per-load notes', value: terms.perLoadNotes})
        }
    } else if (terms.flatRateAmount != null) {
        lines.push({label: 'Flat rate amount', value: formatPrice(terms.flatRateAmount)})
    }

    if (terms.billingMethod !== 'hourly_transportation' && terms.demurrageHourlyRate != null) {
        lines.push({
            label: 'Demurrage hourly rate',
            value: formatPrice(terms.demurrageHourlyRate),
        })
    }

    if (terms.disposalFees.length > 0) {
        terms.disposalFees.forEach((fee, index) => {
            lines.push({
                label: `Disposal fee ${index + 1}: ${fee.wasteType}`,
                value: `${formatDisposalUnit(fee.unit)} @ ${formatPrice(fee.unitRate)}`,
            })
            if (fee.estimatedQuantity != null) {
                lines.push({
                    label: `  Estimated quantity`,
                    value: String(fee.estimatedQuantity),
                })
            }
        })
    }

    if (terms.washoutFee != null) {
        lines.push({label: 'Washout fee', value: formatPrice(terms.washoutFee)})
    }

    if (terms.hseiPercent != null) {
        lines.push({label: 'HSEI percent', value: `${terms.hseiPercent}%`})
    }

    return lines
}

/** Short label/value fields ("Method" / "Rate" / "Disposal" / ...) for a scannable summary row. */
export function getProjectBillingTermsSummaryFields(
    terms: ProjectBillingTerms,
): BillingTermsDisplayLine[] {
    const fields: BillingTermsDisplayLine[] = [
        {label: 'Method', value: projectBillingMethodLabel(terms.billingMethod)},
    ]

    const rate = projectBillingRateLabel(terms)
    if (rate != null) {
        fields.push({label: 'Rate', value: rate})
    }

    if (terms.billingMethod !== 'hourly_transportation' && terms.demurrageHourlyRate != null) {
        fields.push({
            label: 'Demurrage',
            value: `${formatPrice(terms.demurrageHourlyRate)}/hr`,
        })
    }

    if (terms.disposalFees.length > 0) {
        const feeSegments = terms.disposalFees.map(
            (fee) => `${fee.wasteType.toLowerCase()} ${formatPrice(fee.unitRate)}/${fee.unit}`,
        )
        fields.push({label: 'Disposal', value: feeSegments.join(' · ')})
    }

    if (terms.washoutFee != null) {
        fields.push({label: 'Washout', value: formatPrice(terms.washoutFee)})
    }

    if (terms.hseiPercent != null) {
        fields.push({label: 'HSEI', value: `${terms.hseiPercent}%`})
    }

    return fields
}

export function getActiveProjectBillingTerms(
    terms: ProjectBillingTerms[],
): ProjectBillingTerms | null {
    return terms[0] ?? null
}
